import { Icon } from "./Icon.tsx";

/**
 * One schematic 24×24 glyph per bundled sample project, replacing the
 * single `GlobeIcon` that was repeated across all four example buttons on
 * Landing (audit finding: four identical globes read as "AI slop").
 * Following the audit's own suggestion (option a), each glyph is a hint
 * at the network's own character rather than a per-.inp thumbnail render
 * — cheap to maintain, distinct at 16 px, and reuses the shared `Icon`
 * wrapper (24×24 viewBox, `stroke="currentColor"`) so no per-glyph
 * palette or dependency creeps in.
 *
 * Chosen motifs, one per bundled sample:
 *  - `victoria` — ortho grid mesh. Victoria's downtown is famously a
 *    tight rectangular street grid; the network on top of it looks the
 *    same, and the glyph reads as "regular built infrastructure".
 *  - `hague` — winding line. The Hague sample network is organic and
 *    dendritic (see the Landing screenshots in the audit); a single
 *    curved line reads as "flowing / organic" as a counterpoint to the
 *    other three's closed shapes.
 *  - `chaohu` — closed blob. Chao Lake (Chaohu 巢湖) is the landmark
 *    the city takes its name from; a lake-like ellipse gives this one
 *    an unmistakable identity distinct from the other three grids/lines.
 *  - `bogota` — two mountain peaks. Bogotá sits at 2 600 m in an
 *    Andes valley; a mountain silhouette carries the altitude/topography
 *    context that shapes the whole sample project's drainage story.
 *
 * `null` fallback for an unknown key is intentional — a hypothetical
 * future sample that ships before its case lands here still renders the
 * button (label + hover), just without an icon, instead of throwing.
 */
export function SampleThumbnail({ sampleKey }: { sampleKey: string }) {
  switch (sampleKey) {
    case "victoria":
      return (
        <Icon>
          <path d="M4 4h16v16H4z" />
          <path d="M4 10h16M4 16h16M10 4v16M16 4v16" />
        </Icon>
      );
    case "hague":
      return (
        <Icon>
          <path d="M4 12c3 0 3-6 6-6s3 12 6 12 3-6 4-6" />
        </Icon>
      );
    case "chaohu":
      return (
        <Icon>
          <ellipse cx="12" cy="12" rx="8" ry="6" />
        </Icon>
      );
    case "bogota":
      return (
        <Icon>
          <path d="M3 20L9 8L12 12L16 6L21 20Z" />
        </Icon>
      );
    default:
      return null;
  }
}
